import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useAuth } from "../../contexts/AuthContext";
import { useCompatibility } from "./useCompatibility";
import {
  CompatibilityProfile,
  COMMUNICATION_STYLES,
  FormData,
  GENDER_OPTIONS,
  INITIAL_FORM_DATA,
  INTERESTS,
  LOOKING_FOR,
  PERSONALITY_TYPES,
  VALUE_LABELS,
} from "./types";
import {
  Heart,
  Sparkles,
  Moon,
  Sun,
  Feather,
  ArrowLeft,
  ArrowRight,
  Check,
  Loader2,
  User,
} from "lucide-react";

const toProfilePayload = (data: FormData): Partial<CompatibilityProfile> => ({
  ...data,
  age: parseInt(data.age, 10),
});

export const CompatibilityForm: React.FC = () => {
  const navigate = useNavigate();
  const { user, updateCurrentUser } = useAuth();
  const {
    loading,
    currentStep,
    setCurrentStep,
    hasProfile,
    checkUserProfile,
    saveFormData,
  } = useCompatibility(user?.hasCompatibilityProfile);

  const [formData, setFormData] = useState<FormData>(INITIAL_FORM_DATA);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  useEffect(() => {
    if (user?.hasCompatibilityProfile === undefined) {
      checkUserProfile().catch(() =>
        toast.error("Failed to load your profile status."),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (hasProfile) {
      navigate("/matches");
    }
  }, [hasProfile, navigate]);

  const updateField = <K extends keyof FormData>(
    field: K,
    value: FormData[K],
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const updateValue = (key: keyof FormData["values"], value: number) => {
    setFormData((prev) => ({
      ...prev,
      values: { ...prev.values, [key]: value },
    }));
  };

  const toggleArray = (field: "lookingFor" | "interests", value: string) => {
    setFormData((prev) => {
      const current = prev[field];
      const updated = current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value];
      return { ...prev, [field]: updated };
    });
  };

  const validatePage = (step: number): boolean => {
    switch (step) {
      case 1:
        if (!formData.name.trim()) {
          toast.error("Please enter your name");
          return false;
        }
        if (!formData.age || parseInt(formData.age, 10) < 18) {
          toast.error("You must be at least 18 years old");
          return false;
        }
        if (!formData.city.trim()) {
          toast.error("Please enter your city");
          return false;
        }
        return true;

      case 2:
        if (formData.lookingFor.length === 0) {
          toast.error("Please select what you are looking for");
          return false;
        }
        if (
          formData.lookingFor.includes("Other") &&
          !formData.lookingForOther.trim()
        ) {
          toast.error("Please specify what you are looking for");
          return false;
        }
        return true;

      case 3:
        if (formData.interests.length === 0) {
          toast.error("Please select at least one interest");
          return false;
        }
        if (
          formData.interests.includes("Other") &&
          !formData.interestsOther.trim()
        ) {
          toast.error("Please specify your interest");
          return false;
        }
        return true;

      default:
        return true;
    }
  };

  const handleNext = async () => {
    if (!validatePage(currentStep)) return;

    if (currentStep === 4) {
      setIsSubmitting(true);
      try {
        await saveFormData(toProfilePayload(formData), true);
        updateCurrentUser({ hasCompatibilityProfile: true });
        navigate("/matches");
      } catch (error) {
        toast.error("Failed to save profile. Please try again.");
      } finally {
        setIsSubmitting(false);
      }
    } else {
      try {
        await saveFormData(toProfilePayload(formData), false);
        setCurrentStep(currentStep + 1);
      } catch (error) {
        toast.error("Failed to save progress. Please try again.");
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const getStepIcon = (step: number) => {
    switch (step) {
      case 1:
        return <User className="w-5 h-5" />;
      case 2:
        return <Heart className="w-5 h-5" />;
      case 3:
        return <Sparkles className="w-5 h-5" />;
      case 4:
        return <Moon className="w-5 h-5" />;
      default:
        return null;
    }
  };

  const getStepTitle = (step: number) => {
    switch (step) {
      case 1:
        return "About You";
      case 2:
        return "Your Intentions";
      case 3:
        return "Your Essence";
      case 4:
        return "Your Values";
      default:
        return "";
    }
  };

  const getStepSubtitle = (step: number) => {
    switch (step) {
      case 1:
        return "Let's begin your journey of self-discovery";
      case 2:
        return "What calls to your heart?";
      case 3:
        return "Unveil your unique light";
      case 4:
        return "What anchors your soul?";
      default:
        return "";
    }
  };

  const renderPage = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Full Name <span className="text-rose-400">*</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  className="w-full bg-gray-50/80 rounded-2xl px-5 py-3.5 text-base border-2 border-gray-200 focus:border-rose-300 focus:outline-none focus:ring-4 focus:ring-rose-100 transition-all duration-200 placeholder:text-gray-400"
                  placeholder="What name shall we call you?"
                  value={formData.name}
                  onChange={(e) => updateField("name", e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Age <span className="text-rose-400">*</span>
                </label>
                <input
                  type="number"
                  className="w-full bg-gray-50/80 rounded-2xl px-5 py-3.5 text-base border-2 border-gray-200 focus:border-rose-300 focus:outline-none focus:ring-4 focus:ring-rose-100 transition-all duration-200 placeholder:text-gray-400"
                  placeholder="Your age"
                  value={formData.age}
                  onChange={(e) => updateField("age", e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Gender <span className="text-rose-400">*</span>
                </label>
                <div className="flex flex-wrap gap-2">
                  {GENDER_OPTIONS.map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                        formData.gender === opt
                          ? "bg-gradient-to-r from-rose-400 to-purple-400 text-white shadow-lg shadow-rose-200"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                      onClick={() => updateField("gender", opt)}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                City <span className="text-rose-400">*</span>
              </label>
              <input
                type="text"
                className="w-full bg-gray-50/80 rounded-2xl px-5 py-3.5 text-base border-2 border-gray-200 focus:border-rose-300 focus:outline-none focus:ring-4 focus:ring-rose-100 transition-all duration-200 placeholder:text-gray-400"
                placeholder="Where do you call home?"
                value={formData.city}
                onChange={(e) => updateField("city", e.target.value)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date of Birth
              </label>
              <input
                type="date"
                className="w-full bg-gray-50/80 rounded-2xl px-5 py-3.5 text-base border-2 border-gray-200 focus:border-rose-300 focus:outline-none focus:ring-4 focus:ring-rose-100 transition-all duration-200"
                value={formData.birthDate}
                onChange={(e) => updateField("birthDate", e.target.value)}
              />
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <p className="text-gray-500 text-sm italic">
              Select all that resonate with you
            </p>
            <div className="flex flex-wrap gap-3">
              {LOOKING_FOR.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  className={`px-5 py-2.5 rounded-full border-2 text-sm font-medium transition-all duration-200 ${
                    formData.lookingFor.includes(opt)
                      ? "bg-gradient-to-r from-rose-400 to-purple-400 border-transparent text-white shadow-lg shadow-rose-200"
                      : "bg-white border-gray-200 text-gray-600 hover:border-rose-200 hover:bg-rose-50"
                  }`}
                  onClick={() => toggleArray("lookingFor", opt)}
                >
                  {opt}
                </button>
              ))}
            </div>

            {formData.lookingFor.includes("Other") && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Please specify <span className="text-rose-400">*</span>
                </label>
                <input
                  type="text"
                  className="w-full bg-gray-50/80 rounded-2xl px-5 py-3.5 text-base border-2 border-gray-200 focus:border-rose-300 focus:outline-none focus:ring-4 focus:ring-rose-100 transition-all duration-200"
                  placeholder="What else are you seeking?"
                  value={formData.lookingForOther}
                  onChange={(e) =>
                    updateField("lookingForOther", e.target.value)
                  }
                />
              </div>
            )}
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Personality Type <span className="text-rose-400">*</span>
              </label>
              <div className="flex flex-wrap gap-2">
                {PERSONALITY_TYPES.map((type) => (
                  <button
                    key={type}
                    type="button"
                    className={`px-5 py-2.5 rounded-full border-2 text-sm font-medium transition-all duration-200 ${
                      formData.personalityType === type
                        ? "bg-gradient-to-r from-rose-400 to-purple-400 border-transparent text-white shadow-lg shadow-rose-200"
                        : "bg-white border-gray-200 text-gray-600 hover:border-rose-200 hover:bg-rose-50"
                    }`}
                    onClick={() => updateField("personalityType", type)}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Communication Style <span className="text-rose-400">*</span>
              </label>
              <div className="flex flex-wrap gap-2">
                {COMMUNICATION_STYLES.map((style) => (
                  <button
                    key={style}
                    type="button"
                    className={`px-5 py-2.5 rounded-full border-2 text-sm font-medium transition-all duration-200 ${
                      formData.communicationStyle === style
                        ? "bg-gradient-to-r from-rose-400 to-purple-400 border-transparent text-white shadow-lg shadow-rose-200"
                        : "bg-white border-gray-200 text-gray-600 hover:border-rose-200 hover:bg-rose-50"
                    }`}
                    onClick={() => updateField("communicationStyle", style)}
                  >
                    {style}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Interests <span className="text-rose-400">*</span>
              </label>
              <div className="flex flex-wrap gap-2">
                {INTERESTS.map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    className={`px-4 py-2 rounded-full border-2 text-sm font-medium transition-all duration-200 ${
                      formData.interests.includes(opt)
                        ? "bg-gradient-to-r from-rose-400 to-purple-400 border-transparent text-white shadow-lg shadow-rose-200"
                        : "bg-white border-gray-200 text-gray-600 hover:border-rose-200 hover:bg-rose-50"
                    }`}
                    onClick={() => toggleArray("interests", opt)}
                  >
                    {opt}
                  </button>
                ))}
              </div>

              {formData.interests.includes("Other") && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Please specify <span className="text-rose-400">*</span>
                  </label>
                  <input
                    type="text"
                    className="w-full bg-gray-50/80 rounded-2xl px-5 py-3.5 text-base border-2 border-gray-200 focus:border-rose-300 focus:outline-none focus:ring-4 focus:ring-rose-100 transition-all duration-200"
                    placeholder="What are your other interests?"
                    value={formData.interestsOther}
                    onChange={(e) =>
                      updateField("interestsOther", e.target.value)
                    }
                  />
                </div>
              )}
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <p className="text-gray-500 text-sm italic">
              Rate how much each value resonates with you
            </p>

            {Object.entries(VALUE_LABELS).map(([key, label]) => (
              <div key={key} className="bg-gray-50/60 rounded-2xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-700">
                    {label}
                  </span>
                  <span className="text-sm text-gray-400">
                    {formData.values[key as keyof FormData["values"]]}/5
                  </span>
                </div>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((num) => (
                    <button
                      key={num}
                      type="button"
                      className={`flex-1 h-10 rounded-xl transition-all duration-200 ${
                        formData.values[key as keyof FormData["values"]] === num
                          ? "bg-gradient-to-r from-rose-400 to-purple-400 text-white shadow-lg shadow-rose-200"
                          : "bg-white border-2 border-gray-200 text-gray-400 hover:border-rose-200 hover:bg-rose-50"
                      }`}
                      onClick={() =>
                        updateValue(key as keyof FormData["values"], num)
                      }
                    >
                      <span className="text-sm font-semibold">{num}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                About Me (Bio)
              </label>
              <textarea
                className="w-full bg-gray-50/80 rounded-2xl px-5 py-3.5 text-base border-2 border-gray-200 focus:border-rose-300 focus:outline-none focus:ring-4 focus:ring-rose-100 transition-all duration-200 h-24 resize-none placeholder:text-gray-400"
                placeholder="Share a little about your journey..."
                value={formData.bio}
                onChange={(e) => updateField("bio", e.target.value)}
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const isLastStep = currentStep === 4;
  const progressWidth = (currentStep / 4) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-purple-50 to-blue-50 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-rose-200/30 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-200/30 rounded-full blur-3xl"></div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-200/20 rounded-full blur-3xl"></div>

      <div className="w-full max-w-2xl relative z-10">
        {/* Header with brand */}
        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-3 mb-2">
            <div className="bg-gradient-to-r from-rose-400 to-purple-400 p-2 rounded-2xl shadow-lg shadow-rose-200">
              <Heart className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-rose-400 to-purple-400 bg-clip-text text-transparent">
              Astria
            </h1>
          </div>
          <p className="text-sm text-gray-500 flex items-center justify-center gap-2">
            <Sparkles className="w-4 h-4 text-rose-400" />
            <span>ANIMO DIARIO — Your Journey of Self-Discovery</span>
            <Sparkles className="w-4 h-4 text-rose-400" />
          </p>
        </div>

        <div className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl shadow-rose-100/50 border border-white/50 overflow-hidden">
          {/* Progress header */}
          <div className="px-8 pt-8 pb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  {getStepIcon(currentStep)}
                  <span className="text-sm font-semibold text-rose-400">
                    Step {currentStep}
                  </span>
                  <span className="text-xs text-gray-400">of 4</span>
                </div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {getStepTitle(currentStep)}
                </h2>
                <p className="text-sm text-gray-500">
                  {getStepSubtitle(currentStep)}
                </p>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-gray-300">
                  {currentStep}/4
                </div>
              </div>
            </div>

            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-rose-400 to-purple-400 transition-all duration-500 ease-out rounded-full"
                style={{ width: `${progressWidth}%` }}
              />
            </div>
          </div>

          {/* Content */}
          <div className="px-8 pb-6 max-h-[55vh] overflow-y-auto scrollbar-thin scrollbar-thumb-rose-200 scrollbar-track-transparent">
            {renderPage()}
          </div>

          {/* Footer actions */}
          <div className="px-8 py-5 border-t border-gray-100 bg-white/50 flex justify-between items-center">
            <button
              type="button"
              className={`flex items-center gap-2 px-6 py-3 rounded-2xl font-medium transition-all duration-200 ${
                currentStep === 1
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:shadow-md"
              }`}
              onClick={handleBack}
              disabled={currentStep === 1}
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>

            <button
              type="button"
              className={`flex items-center gap-2 px-8 py-3 rounded-2xl font-medium text-white transition-all duration-200 shadow-lg shadow-rose-200 hover:shadow-xl hover:shadow-rose-300 hover:scale-[1.02] active:scale-95 ${
                loading || isSubmitting
                  ? "bg-gradient-to-r from-rose-300 to-purple-300 cursor-not-allowed"
                  : "bg-gradient-to-r from-rose-400 to-purple-400 hover:from-rose-500 hover:to-purple-500"
              }`}
              onClick={handleNext}
              disabled={loading || isSubmitting}
            >
              {loading || isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  {isLastStep ? (
                    <>
                      <Check className="w-5 h-5" />
                      Complete Journey
                    </>
                  ) : (
                    <>
                      Continue
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </>
              )}
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-4">
          <p className="text-xs text-gray-400 flex items-center justify-center gap-2">
            <Feather className="w-3 h-3" />
            <span>
              "It is okay to take up space even when you feel a bit uncertain
              today."
            </span>
            <Feather className="w-3 h-3" />
          </p>
          <p className="text-xs text-gray-300 mt-1">— Astria</p>
        </div>
      </div>
    </div>
  );
};

export default CompatibilityForm;
